import { InputAdornment, SelectChangeEvent, TextField } from "@mui/material";
import React, { ChangeEventHandler, FC, useState } from "react";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import FeatherIcon from "feather-icons-react";

import { useGetCompaniesBasics } from "@api";
import { Box, Button, FormControl, MenuItem, Select, Stack } from "@components/base";
import { useUserStore } from "@store/user";

export const COMPANY_FILTER_DEFAULT_VALUE = "all";

interface UsersTableToolbarProps {
  companyNameFilter: string;
  onCompanyNameFilterChange: (searchText: string) => void;
  companyIdFilter: string;
  onCompanyIdFilterChange: (companyId: string) => void;
  onCreateUserButtonClick: () => void;
}

const UsersTableToolbar: FC<UsersTableToolbarProps> = ({
  companyNameFilter,
  onCompanyNameFilterChange,
  companyIdFilter,
  onCompanyIdFilterChange,
  onCreateUserButtonClick,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [search, setSearch] = useState(false);
  const { companiesBasics, companiesBasicsEnabled } = useGetCompaniesBasics(user);

  const handleUserNameFilterChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    const searchText = e.target.value;

    onCompanyNameFilterChange(searchText);
  };

  const handleCompanyFilterChange = (e: SelectChangeEvent<string>): void => {
    const { value } = e.target;

    onCompanyIdFilterChange(value);
  };

  const handleCreateUserButtonClick = (): void => {
    onCreateUserButtonClick();
  };

  return (
    companiesBasicsEnabled && (
      <Stack direction="row" gap={2} justifyContent="flex-end">
        <Button
          variant="text"
          startIcon={<FeatherIcon icon="user-plus" size={20} />}
          onClick={handleCreateUserButtonClick}
          size="small"
        >
          {t("usersManagement.userEditor.title.add")}
        </Button>
        {!search && (
          <Button
            variant="text"
            startIcon={<FeatherIcon icon="search" size={20} />}
            size="small"
            onClick={() => setSearch(true)}
          >
            {t("common.search")}
          </Button>
        )}
        {search && (
          <TextField
            variant="filled"
            placeholder={t("usersManagement.userEditor.title.search")}
            onChange={handleUserNameFilterChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            size="small"
            value={companyNameFilter}
            autoFocus
          />
        )}
        <Box width={180}>
          <FormControl fullWidth>
            <Select
              name="companyId"
              variant="filled"
              onChange={(e) => handleCompanyFilterChange(e as SelectChangeEvent)}
              size="small"
              value={companyIdFilter || COMPANY_FILTER_DEFAULT_VALUE}
            >
              <MenuItem key="all" value={COMPANY_FILTER_DEFAULT_VALUE}>
                {t("common.allEntities")}
              </MenuItem>
              {companiesBasics?.items.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
    )
  );
};

export default UsersTableToolbar;
